import { FC, useLayoutEffect, useState, useEffect } from "react";
import { useNotification } from "../../../../assets/hooks/useNotification";
import {
    Permissions,
    StateActionPermission,
    TypeAction,
} from "../../../../models/models";
import {
    useCreateMigratedProductMutation,
    useGetMigrationProductsDetailsQuery,
    useUpdateMigratedProductMutation,
} from "../../../../redux/RTKQuery/api";
import { ActualProduct, EditMigratedProduct } from ".././model";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import TextInput from "../../../../assets/components/Inputs/TextInput/TextInput";
import ActionButtons from "../../../../assets/components/ActionButtons/ActionButtons";
import { hasPermission } from "../../../../assets/helpers/helpers";
import { useAppDispatch, useAppSelector } from "../../../../assets/hooks/hooks";
import "./MigratedProductSubRow.scss";
import { actionText } from "../../../../text";
import Select from "../../../../assets/components/Select/Select";
import MigratedInformPanel from "../../InformPanel/MigratedInformPanel";
import { uiSlicedActions } from "../../../../redux/ui-slice";
import { toast } from "react-toastify";

const validateSchema = Yup.object().shape({
    upclickProductName: Yup.string().required("Required"),
    productId: Yup.string().required("Required"),
    upclickProductId: Yup.string()
        .required("Required")
        .max(10, "Max 10 characters"),
});

const MigratedProductSubRow: FC<EditMigratedProduct> = ({
    actualProducts = [],
    productId,
    productName,
    upclickProductId,
    upclickProductName,
    closeFormHandler,
}) => {
    const dispatch = useAppDispatch();

    const {
        data: licenseStats,
        isLoading,
        isError,
    } = useGetMigrationProductsDetailsQuery(
        {
            upclickProductId,
        },
        {
            skip: productId === -1,
        }
    );

    useEffect(() => {
        dispatch(uiSlicedActions.setLoading(isLoading ? "pending" : "hidden"));
    }, [isLoading, dispatch]);

    useLayoutEffect(() => {
        if (isError) {
            toast.error("Error fetching product details");
        }
    }, [isError]);

    const [updatedMigrationProduct] = useUpdateMigratedProductMutation({
        fixedCacheKey: "update_migrated_product",
    });

    const [createdMigrationProduct] = useCreateMigratedProductMutation({
        fixedCacheKey: "create_migrated_product",
    });

    const { ConfirmationMessageBox } = useNotification();

    const [typeAction] = useState<TypeAction>(
        productId === -1 ? { type: "create" } : { type: "update" }
    );

    const [actualProductId, setActualProductId] = useState<ActualProduct["id"]>(
        productId === -1 ? actualProducts[0].id : productId
    );

    const [isUpdatedTypeAction] = useState<boolean>(
        typeAction.type === "update"
    );

    const user = useAppSelector((state) => state.initializeApp);

    const isAccessPermission = hasPermission(
        user.permissions,
        Permissions.PRODUCT_EDIT
    );

    const submitHandler = async (values: {
        productId: number;
        productName: string;
        upclickProductName: string;
        upclickProductId: string;
    }) => {
        const value = await ConfirmationMessageBox({
            textContent: actionText(
                typeAction.type as StateActionPermission,
                "migrated product"
            ),
        });

        if (value === "yes") {
            if (typeAction.type === "create") {
                createdMigrationProduct({
                    productMappings: [
                        {
                            upclickProductId: values.upclickProductId,
                            alias: values.upclickProductName,
                            productId: actualProductId,
                        },
                    ],
                    options: {
                        forceOverride: true,
                    },
                });
            } else if (isUpdatedTypeAction) {
                updatedMigrationProduct({
                    upclickProductId: values.upclickProductId,
                    alias: values.upclickProductName,
                    productId: actualProductId,
                });
            }
        }
    };

    return (
        <div>
            <MigratedInformPanel
                cancelledLicensesCount={
                    licenseStats?.cancelledLicensesCount ?? 0
                }
                expiredLicensesCount={licenseStats?.expiredLicensesCount ?? 0}
                migratedLicensesCount={licenseStats?.migratedLicensesCount ?? 0}
                totalLicensesCount={licenseStats?.totalLicensesCount ?? 0}
            />

            <Formik
                initialValues={{
                    productId,
                    upclickProductId,
                    productName,
                    upclickProductName,
                }}
                enableReinitialize
                validationSchema={validateSchema}
                onSubmit={submitHandler}
            >
                {({ isSubmitting, isValid, values, errors }) => {
                    return (
                        <Form className="form_migrated_products">
                            <div className="form_migrated_products__row">
                                <Field
                                    id="upclickProductId"
                                    label={"Upclick ID *"}
                                    name={"upclickProductId"}
                                    as={TextInput}
                                    readOnly={!isAccessPermission}
                                    errors={errors.upclickProductId}
                                />

                                <Field
                                    id="upclickProductName"
                                    label={"Original Product Name *"}
                                    name="upclickProductName"
                                    as={TextInput}
                                    readOnly={!isAccessPermission}
                                    errors={errors.upclickProductName}
                                />

                                <Select
                                    currentId={actualProductId}
                                    options={actualProducts}
                                    label="Actual Product Name"
                                    onChange={setActualProductId}
                                    readOnly={!isAccessPermission}
                                />
                            </div>

                            <div className="form_migrated_products__footer">
                                <ActionButtons
                                    className="form_migrated_products__button_submit"
                                    actionButtons={[
                                        {
                                            type: "button",
                                            disabled: isSubmitting,
                                            name: "close",
                                            role: "secondary",
                                            onClick: () => {
                                                closeFormHandler(productId);
                                            },
                                        },
                                        {
                                            type: "submit",
                                            disabled: isSubmitting || !isValid,
                                            name: "save",
                                            role: "primary",
                                            hidden: !isAccessPermission,
                                        },
                                    ]}
                                />
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default MigratedProductSubRow;
